import { Component, inject } from '@angular/core';
import { tablePropInt } from '../shared/table-interface';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { PatientDetailsKeyService } from '../patient-sidedetails/patient-details-key.service';
import { commonImports } from '../shared/table-interface';
import { UtilityService } from '../shared/utility.service';
import { CommonModule, JsonPipe } from '@angular/common';
import { CodeableConcept2Pipe } from '../shared/codeable-concept2.pipe';
import { AllergyIntolerance } from 'fhir/r4';
import { AdmittedPatientsComponent } from '../admitted-patients/admitted-patients.component';
import { CodeableRef2Pipe } from '../shared/codeable-ref2.pipe';
import { References2Pipe } from '../shared/references2.pipe';
import { LinkInReferencesService } from '../shared/link-in-references.service';
import { fetchFromReferencePipe } from '../shared/Fetch.pipe';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { baseStatusStyles } from '../shared/statusUIIcons';
import { DetailBaseComponent } from '../shared/detail-base/detail-base.component';
import { AuthService, capacityObject } from '../shared/auth/auth.service';
import { EncounterServiceService } from '../patient-wrapper/encounter-service.service';
import { AddAllergyComponent } from './add-allergy/add-allergy.component';
import { PriceFormatPipe } from "../shared/price-format.pipe";
import { PatientAddressPipe } from '../shared/pipes/patient-address.pipe';
import { PatientAgePipe } from '../shared/pipes/patient-age.pipe';
import { PatientContactsPipe } from '../shared/pipes/patient-contacts.pipe';
import { PatientNamePipe } from '../shared/pipes/patient-name.pipe';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { ResourceStatusChangesService } from '../resource-status-changes/resource-status-changes.service';
import { DetailzViewzComponent } from '../detailz-viewz/detailz-viewz.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';

@Component({
  selector: 'app-allergy',
  imports: [...commonImports,
    CommonModule,
    EmptyStateComponent,
    JsonPipe, CodeableConcept2Pipe, CodeableRef2Pipe, References2Pipe, fetchFromReferencePipe,
    MatProgressSpinnerModule, DetailBaseComponent, PriceFormatPipe],
  templateUrl: './allergy.component.html',
  styleUrl: './allergy.component.scss',
  providers: [PatientNamePipe, PatientContactsPipe, PatientAgePipe, PatientAddressPipe],

})
export class AllergyComponent extends AdmittedPatientsComponent implements tablePropInt {
  statusStyles = baseStatusStyles;
  islink = inject(LinkInReferencesService);
  utilityService: UtilityService = inject(UtilityService);
  route: ActivatedRoute = inject(ActivatedRoute);
  patientName!: Observable<string | null>;
  patientId!: string | null;

  // tablePropInt compliance
  immutableLevelTableData: any[] = [];
  tableDataLevel2: BehaviorSubject<any> = new BehaviorSubject([]);
  references: Map<string, any> = new Map();
  tableFilter: Map<string, any[]> = new Map();
  tableFilterFormControlObject: { [key: string]: FormGroup } = {};
  tableColumns: string[] = [];
  patientOpenAndClose: PatientDetailsKeyService = inject(PatientDetailsKeyService);
  resourceStatusChanges = inject(ResourceStatusChangesService);

  clinicalStatusCodes = this.resourceStatusChanges.getStatusCodes('AllergyIntolerance_clinicalStatus');
  verificationStatusCodes = this.resourceStatusChanges.getStatusCodes('AllergyIntolerance_verificationStatus');

  detailsBuilder: DetailsBuilderObject = {
    resourceName: 'Allergy / Intolerance',
    resourceIcon: 'coronavirus',
    specialHeader: {
      strongSectionKey: 'code',
      iconSectionKeys: ['clinicalStatus', 'verificationStatus', 'criticality'],
      contentSectionKeys: ['patient']
    },
    groups: [
      {
        groupName: 'General Information',
        groupIcon: 'info',
        groupKeys: ['clinicalStatus', 'verificationStatus', 'type', 'category', 'criticality', 'code', 'patient']
      },
      {
        groupName: 'Events & Tracking',
        groupIcon: 'event',
        groupKeys: ['onsetDateTime', 'onsetAge', 'recordedDate', 'recorder']
      },
      {
        groupName: 'Reactions',
        groupIcon: 'warning',
        groupKeys: ['reaction']
      }
    ]
  };

  getPatientName(): void {
    if (!this.getPatientId()) return;
    this.patientName = this.utilityService.getPatientName(this.patientId!)
  }

  getPatientId(): any {
    return this.patientId = this.route.parent?.snapshot.params['id'] || this.utilityService.getPatientIdFromRoute();
  }

  connectTableDataSource(): void { }
  subscribeToResolver(): void { }

  //for determining actions available to the user
  authService = inject(AuthService);
  user: any = null;
  encounterService = inject(EncounterServiceService);
  capacityObject = capacityObject;

  override ngOnInit() {
    this.authService.user.subscribe((user) => {
      this.user = user;
    })

    const filters = new Map([
      ['Status', ['confirmed', 'presumed', 'unconfirmed', 'refuted', 'entered-in-error']],
      ['criticality', ['low', 'high', 'unable-to-assess']],
      ['category', ['food', 'medication', 'environment', 'biologic']]
    ]);
    this.organizingSearchFilters(filters);
    this.tableFilter = filters; // for tablePropInt compliance
    this.tableFilterFormControlObject = this.tableHeaderFiltersFormControlObject;

    this.displayedColumns = [
      'code',
      'criticality',
      'category',
      'verificationStatus',
      'action'
    ]
    this.tableColumns = this.displayedColumns;

    this.getPatientId();
    this.getPatientName();

    if (this.patientId) {
      this.initiateDataSourceSubscription(
        this.utilityService.threeTierQueryAndSyncToState$<AllergyIntolerance>({
          resourceType: 'AllergyIntolerance',
          scope: 'patient',
          patientId: this.patientId,
          patientScopeSearchParam: 'patient',
          urlSearchObject: {
            patient: `Patient/${this.patientId}`,
          },
          querySearchObject: [
            { key: 'patient.reference', value: [`Patient/${this.patientId}`, `${this.backendUrl}/Patient/${this.patientId}`] }
          ],
          tier1FollowNextLinks: true
        })
      );
    }
  }

  addAllergy() {
    this.dialog.open(AddAllergyComponent, {
      maxWidth: '650px',
      maxHeight: '93vh',
    })
  }

  showRow(row: any): void {
    const rawResource = row.rawResource || row;
    this.dialog.open(DetailzViewzComponent, {
      maxWidth: '650px',
      maxHeight: '93vh',
      disableClose: true,
      data: {
        resourceData: rawResource,
        detailsBuilderObject: this.detailsBuilder,
      }
    });
  }

  changeStatus(row: any, statusType: 'clinicalStatus' | 'verificationStatus', newStatus: string) {
    const id = row.rawResource?.id || row.id;
    if (!id) return;
    this.resourceStatusChanges.changeResourceStatus('AllergyIntolerance', id, newStatus, 'auto', statusType)
      .subscribe({
        next: (resp) => {
          console.log('Status updated successfully', resp);
          if (statusType === 'clinicalStatus') {
            (row.rawResource || row).clinicalStatus = { coding: [{ code: newStatus }] };
          } else {
            (row.rawResource || row).verificationStatus = { coding: [{ code: newStatus }] };
          }
        }
      });
  }

}
